import React, { useEffect, useSyncExternalStore } from 'react';
import {
  getImpersonatorFromToken,
  subscribeToToken,
  clearAll,
} from '../utils/TokenUtils';

/**
 * Says, unmissably, that this is a manager looking at a homeowner's
 * account rather than the homeowner themselves.
 *
 * **An impersonated session that looks identical to a real one is how a
 * support tool becomes an incident.** Somebody who forgets which window
 * they are in will eventually say something to a homeowner about data
 * they should not have been reading, or file a note under the wrong
 * account. The banner is the cheapest possible defence and the one the
 * person actually needs.
 *
 * It is **not** a security control. The server refuses every mutating
 * request on a session carrying `act`, in `member_auth::authenticate`,
 * before any handler runs. This is here so the operator knows; the rule
 * holds whether or not it renders.
 */
const ImpersonationBanner: React.FC = () => {
  // **Subscribed, not read once.** This sits outside `<Routes>` so it
  // shows on every page — which also means React does not re-render it
  // when the route changes. The first version read the token at mount and
  // never again, so arriving from `/impersonate`, which stores the token
  // in an effect and *then* navigates, rendered no banner at all: the
  // manager got a silent, unmarked view of a homeowner's portal.
  //
  // Caught in the browser, not by a test — the components were correct
  // in isolation and wrong together. `subscribeToToken` already exists
  // for exactly this, and drives `MemberProvider` the same way.
  const impersonator = useSyncExternalStore(
    subscribeToToken,
    getImpersonatorFromToken
  );

  // **Marks the document, so the layout can make room.** The banner is
  // `fixed`, and so is the app's sidebar (`fixed inset-y-0`) — a sticky
  // or fixed bar simply covers it, clipping the logo. Fixed elements
  // ignore body padding, so the sidebar has to be shifted explicitly;
  // `index.css` does it from this attribute.
  //
  // An attribute on <html> rather than a wrapper element because the
  // banner renders outside the layout, on purpose, so that it appears on
  // every page.
  useEffect(() => {
    if (!impersonator) return;
    document.documentElement.setAttribute('data-impersonating', 'true');
    return () => {
      document.documentElement.removeAttribute('data-impersonating');
    };
  }, [impersonator]);

  if (!impersonator) return null;

  const end = () => {
    clearAll();
    // Back to the sign-in door rather than to front-porch: this window
    // holds a member session, and the manager's own session lives on the
    // other origin, untouched.
    window.location.assign('/login');
  };

  return (
    <div
      role="status"
      className="fixed inset-x-0 top-0 z-50 flex h-10 items-center flex-wrap justify-between gap-2 bg-amber-500 px-4 text-sm font-medium text-black"
    >
      <span>
        <strong>Read-only view.</strong> You are seeing this homeowner’s
        portal as they see it. Nothing here can be changed, and this view
        is recorded.
      </span>
      <button
        type="button"
        onClick={end}
        className="rounded bg-black/80 px-3 py-1 text-xs font-semibold text-white hover:bg-black"
      >
        End view
      </button>
    </div>
  );
};

export default ImpersonationBanner;
